import React, { useState } from "react";
import { CloudMoon, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import CosmicBackground from "../components/astro/CosmicBackground";
import api from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";

export default function DreamReading() {
  const { lang } = useLang();
  const { openBooking } = useBooking();
  const [dream, setDream] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const interpret = async () => {
    if (dream.trim().length < 15) { toast.error("Please describe your dream in a little more detail"); return; }
    setLoading(true);
    try {
      const r = await api.post("/astro/dream", { dream: dream.trim(), lang });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not interpret the dream right now.");
    } finally { setLoading(false); }
  };

  return (
    <div className="relative" data-testid="dream-page">
      <Seo
        title="Dream Interpretation — Traditional Symbolism"
        description="Describe your dream and receive its traditional Indian and Shakta-Tantric symbolism with gentle spiritual guidance and a suggested practice."
        path="/astrology/dream"
        keywords={["dream meaning Hindu", "swapna shastra", "dream interpretation spiritual"]}
      />
      <CosmicBackground />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
        <div className="text-center mb-9">
          <span className="badge-gold mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-serif uppercase tracking-[0.28em]">
            <CloudMoon className="h-3.5 w-3.5" /> Dream Interpretation
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-3">
            <span className="text-gradient-flame">What was your dream telling you?</span>
          </h1>
          <p className="text-sm text-tara-body max-w-xl mx-auto">
            Traditional swapna-shastra and Shakta symbolism, offered gently — never as a warning or a prediction.
          </p>
        </div>

        <div className="glass-liquid rounded-[1.8rem] p-7">
          <textarea
            value={dream} onChange={(e) => setDream(e.target.value)} rows={5}
            placeholder="Describe your dream — what you saw, who was there, colours, water, fire, animals, temples, how you felt when you woke."
            className="w-full rounded-2xl border border-tara-teal/25 bg-white/85 p-4 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-teal/60"
            data-testid="dream-input"
          />
          <button onClick={interpret} disabled={loading} className="btn-sacred mt-4 flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-serif font-semibold" data-testid="dream-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Interpret my dream
          </button>
        </div>

        {result && (
          <div className="mt-7 space-y-5" data-testid="dream-result">
            {(result.symbols || []).length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2">
                {result.symbols.map((s, i) => (
                  <div key={i} className="glass rounded-2xl p-5" data-testid={`dream-symbol-${i}`}>
                    <p className="font-display text-lg text-tara-ink mb-1">{s.symbol}</p>
                    <p className="text-sm text-tara-body leading-relaxed">{s.meaning}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="glass rounded-[1.5rem] p-7" data-testid="dream-interpretation">
              <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-4">Interpretation</h3>
              {result.interpretation.split("\n").filter(Boolean).map((p, i) => (
                <p key={i} className="mb-3 text-sm leading-relaxed text-tara-body last:mb-0">{p}</p>
              ))}
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="glass rounded-[1.5rem] p-6">
                <h4 className="font-display text-xl font-semibold text-tara-ink mb-2">Spiritual guidance</h4>
                <p className="text-sm text-tara-body leading-relaxed">{result.spiritual_guidance}</p>
              </div>
              <div className="glass-liquid rounded-[1.5rem] p-6">
                <h4 className="font-display text-xl font-semibold text-tara-ink mb-2">A gentle practice</h4>
                <p className="text-sm text-tara-body leading-relaxed">{result.suggested_practice}</p>
                <button onClick={() => openBooking("mantra-diksha", "Guidance about a dream")}
                        className="btn-sacred mt-5 flex items-center gap-2 rounded-full px-5 py-3 text-xs font-serif font-semibold"
                        data-testid="dream-consult-btn">
                  🙏 Speak with Shivam Chatterjee
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
